import React, { useState, useEffect, useContext, useCallback } from 'react';
import { deleteUser } from 'firebase/auth';
import { AuthContext } from '../contexts/AuthContext';
import ReviewCard from './ReviewCard';
import { REVIEW_TITLE_MAX_LENGTH, REVIEW_TEXT_MAX_LENGTH } from '../utils/validation';
import { firebaseAuth } from '../firebase';
import {
  deleteReview as deleteReviewDoc,
  getUserReviews as getMyReviews,
  removeCurrentUserAccount,
  updateReview as updateReviewDoc,
} from '../services/firestoreService';

function UserProfile({ onClose }) {
  const { user, logout } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', text: '', media: [] });
  const [removingAccount, setRemovingAccount] = useState(false);

  const fetchUserReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await getMyReviews();
      setReviews(list);
    } catch (err) {
      setError(err?.message || 'Something went wrong, please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserReviews();
  }, [fetchUserReviews]);

  const handleEdit = (review) => {
    setEditingReview(review.id);
    setEditForm({
      title: review.title || '',
      text: review.text,
      media: (review.media_urls || []).map((url) => ({
        type: 'existing',
        url,
        preview: url
      }))
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const trimmedTitle = (editForm.title || '').trim();
    const trimmedText = (editForm.text || '').trim();

    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }

    if (!trimmedText) {
      setError('Review text is required');
      return;
    }

    if (trimmedTitle.length > REVIEW_TITLE_MAX_LENGTH) {
      setError(`Title cannot exceed ${REVIEW_TITLE_MAX_LENGTH} characters`);
      return;
    }

    if (trimmedText.length > REVIEW_TEXT_MAX_LENGTH) {
      setError(`Review text cannot exceed ${REVIEW_TEXT_MAX_LENGTH} characters`);
      return;
    }

    try {
      await updateReviewDoc(editingReview, {
        title: trimmedTitle,
        text: trimmedText,
        media: editForm.media || [],
      });

      setEditingReview(null);
      fetchUserReviews();
    } catch (err) {
      setError(err?.message || 'Something went wrong, please try again.');
    }
  };

  const handleDelete = async (reviewId) => {
    try {
      await deleteReviewDoc(reviewId);

      fetchUserReviews();
    } catch (err) {
      setError(err?.message || 'Something went wrong, please try again.');
    }
  };

  const handleRemoveAccount = async () => {
    if (!window.confirm('Are you sure you want to remove your account? This cannot be undone.')) return;
    setRemovingAccount(true);
    try {
      await removeCurrentUserAccount();
      if (firebaseAuth?.currentUser) {
        try {
          await deleteUser(firebaseAuth.currentUser);
        } catch {
          // Deletion can require re-auth; keeping logout still clears local session.
        }
      }
      logout();
      onClose();
    } catch (err) {
      setError(err?.message || 'Something went wrong, please try again.');
      setRemovingAccount(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 sm:px-4 sm:py-8 sm:px-6">
      <div className="min-h-screen sm:min-h-0 mx-auto sm:max-w-3xl bg-white p-5 shadow-2xl sm:rounded-[32px] sm:p-10">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">My Account</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Your reviews</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">✕</button>
        </div>

        <div className="mt-6">
          <div className="mb-6 rounded-3xl bg-slate-50 p-4 border border-slate-200">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{user?.email}</span>
            </p>
            <button
              type="button"
              onClick={handleRemoveAccount}
              disabled={removingAccount}
              className="mt-3 w-full rounded-3xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {removingAccount ? 'Removing...' : 'Remove Account'}
            </button>
          </div>

          {loading && (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 rounded-3xl bg-slate-200 animate-pulse"></div>
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 mb-4">
              {error}
            </div>
          )}

          {!loading && reviews.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">You haven't submitted any reviews yet.</p>
            </div>
          )}

          {!loading && reviews.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-slate-600">
                {reviews.length} review{reviews.length !== 1 ? 's' : ''}
              </p>
              {reviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isEditing={editingReview === review.id}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  onSaveEdit={handleEditSubmit}
                  onCancelEdit={() => setEditingReview(null)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-3xl bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 sm:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
